import { arrayMove } from '@dnd-kit/sortable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';

import { isTabAllowed, useGetLoggedInUser } from '@revelio/auth';

import { getAllPreferences } from '../../../shared/user-preferences/api/user-preferences-api';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../../../shared/user-preferences/types';
import {
  enqueuePageOrderSave,
  writePageOrderToCache,
} from '../../../shared/user-preferences/utils/page-order-queue';
import { type Page, PageType } from '../components/PageConfigProvider/types';
import { usePageManager } from '../components/PageManagerProvider/PageManagerProvider';
import {
  ANALYTICS_VIEWS,
  type AnalyticsEntity,
} from '../utils/revelio-primary-filter-entity-switch';
import {
  buildPathFromPage,
  entityTypeToFilterId,
} from '../utils/routing-helpers';

export const MAX_BAR_TABS = 6;

export type TabKind = 'analytics' | 'custom';

export type TabItem = {
  id: string;
  kind: TabKind;
  label: string;
  path: string;
  /** Original payload for callers that need it (e.g. menu rename/delete). */
  page?: Page;
};

export const entityToScope = (entity: AnalyticsEntity): PreferenceScope => {
  const map: Record<AnalyticsEntity, PreferenceScope> = {
    company: PreferenceScope.COMPANY,
    geography: PreferenceScope.GEOGRAPHY,
    role: PreferenceScope.ROLE,
  };
  return map[entity];
};

const analyticsTabId = (viewName: string) => `analytics:${viewName}`;
export const customTabId = (pageId: string) => `custom:${pageId}`;

// Stable reference so a render where allPrefs lacks data doesn't churn the
// downstream useMemo deps with a fresh empty array each pass.
const EMPTY_PAGE_ORDER: string[] = [];

const mergeWithStoredOrder = (
  available: TabItem[],
  storedIds: string[]
): TabItem[] => {
  const byId = new Map(available.map((t) => [t.id, t]));
  const seen = new Set<string>();
  const ordered: TabItem[] = [];
  for (const id of storedIds) {
    const tab = byId.get(id);
    if (tab && !seen.has(id)) {
      ordered.push(tab);
      seen.add(id);
    }
  }
  for (const tab of available) {
    if (!seen.has(tab.id)) {
      ordered.push(tab);
    }
  }
  return ordered;
};

export const useTabOrder = (entity: AnalyticsEntity) => {
  const { loggedInUser } = useGetLoggedInUser();
  const { pages } = usePageManager();
  const queryClient = useQueryClient();

  const scope = entityToScope(entity);

  const { data: allPrefs } = useQuery<
    Record<PreferenceScope, Partial<UserPreferences>>
  >({
    queryKey: ['user-preferences'],
    queryFn: getAllPreferences,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const availableTabs = useMemo<TabItem[]>(() => {
    const userTabs = loggedInUser?.tabs ?? [];
    const analytics: TabItem[] = ANALYTICS_VIEWS.filter((view) =>
      isTabAllowed(view.serverTab, userTabs)
    ).map((view) => ({
      id: analyticsTabId(view.name),
      kind: 'analytics',
      label: view.name,
      path: `/${view.paths[entity]}`,
    }));

    const entityFilterId = entityTypeToFilterId(entity);
    const custom: TabItem[] = pages
      .filter(
        (p) =>
          p.type !== PageType.REPORT &&
          p.tabs?.[0]?.tabContent?.primaryFilter?.id === entityFilterId
      )
      .map((page) => ({
        id: customTabId(page.id),
        kind: 'custom',
        label: page.name,
        path: buildPathFromPage(page),
        page,
      }));

    return [...analytics, ...custom];
  }, [loggedInUser?.tabs, pages, entity]);

  // Holds the in-flight order while a drag is active.
  const [draftIds, setDraftIds] = useState<string[] | null>(null);

  const storedIds =
    allPrefs?.[scope]?.[UserPreferenceKey.PageOrder] ?? EMPTY_PAGE_ORDER;

  type AllPrefs = Record<PreferenceScope, Partial<UserPreferences>>;
  type PersistVars = {
    ids: string[];
    targetScope: PreferenceScope;
    previous: AllPrefs | undefined;
    sequence: number;
  };

  // Bumps on every persist so onError/onSettled effects from stale (older)
  // mutations can no-op — prevents an older save's rollback or refetch from
  // clobbering a newer optimistic write.
  const sequenceRef = useRef(0);

  const { mutate: persistOrder } = useMutation<unknown, Error, PersistVars>({
    mutationFn: ({ ids, targetScope }) =>
      enqueuePageOrderSave(targetScope, ids),
    onError: (_err, { previous, sequence }) => {
      if (sequence !== sequenceRef.current) return;
      if (previous) queryClient.setQueryData(['user-preferences'], previous);
    },
    onSettled: (_data, _err, { sequence }) => {
      if (sequence !== sequenceRef.current) return;
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });

  const dispatchPersist = useCallback(
    (ids: string[]) => {
      const previous = writePageOrderToCache(queryClient, scope, ids);
      sequenceRef.current += 1;
      persistOrder({
        ids,
        targetScope: scope,
        previous,
        sequence: sequenceRef.current,
      });
    },
    [queryClient, persistOrder, scope]
  );

  const activeIds = draftIds ?? storedIds;

  const orderedTabs = useMemo(
    () => mergeWithStoredOrder(availableTabs, activeIds),
    [availableTabs, activeIds]
  );

  const barTabs = orderedTabs.slice(0, MAX_BAR_TABS);
  const menuTabs = orderedTabs.slice(MAX_BAR_TABS);

  const previewMove = useCallback(
    (activeId: string, overId: string) => {
      // The menu trigger droppable only auto-opens the popover. Skipping the
      // reorder here keeps the bar stable at the bar/trigger boundary where
      // collision detection would otherwise oscillate the tab in and out.
      if (overId === 'menu') return;
      const fromIdx = orderedTabs.findIndex((t) => t.id === activeId);
      if (fromIdx < 0) return;
      const targetIdx = orderedTabs.findIndex((t) => t.id === overId);
      if (targetIdx < 0 || targetIdx === fromIdx) return;
      const next = arrayMove(orderedTabs, fromIdx, targetIdx).map((t) => t.id);
      setDraftIds(next);
    },
    [orderedTabs]
  );

  /** Persist the current drag order to the server (optimistic — does not wait). */
  const saveOrder = useCallback(() => {
    const finalIds = draftIds ?? storedIds;
    setDraftIds(null);
    dispatchPersist(finalIds);
  }, [draftIds, storedIds, dispatchPersist]);

  /**
   * Drop a tab on the menu trigger without hovering a specific menu slot:
   * move it to the first menu position so the action is meaningful.
   */
  const commitDropOnMenu = useCallback(
    (tabId: string) => {
      const fromIdx = orderedTabs.findIndex((t) => t.id === tabId);
      if (fromIdx < 0 || fromIdx >= MAX_BAR_TABS) {
        saveOrder();
        return;
      }
      const finalIds = arrayMove(orderedTabs, fromIdx, MAX_BAR_TABS).map(
        (t) => t.id
      );
      setDraftIds(null);
      dispatchPersist(finalIds);
    },
    [orderedTabs, saveOrder, dispatchPersist]
  );

  /** Resets tab order to default (Overview, Compositions, Sentiment, Pay, etc.). */
  const resetOrder = useCallback(() => {
    setDraftIds(null);
    dispatchPersist([]);
  }, [dispatchPersist]);

  /** Discard the drag preview without persisting. */
  const cancelPreview = useCallback(() => setDraftIds(null), []);

  return {
    orderedTabs,
    barTabs,
    menuTabs,
    previewMove,
    saveOrder,
    commitDropOnMenu,
    cancelPreview,
    resetOrder,
  };
};
